// API Client utility for making typed requests to Prosci Connect API

import type {
  ApiResponse,
  JsonApiResource,
  ApiError,
  QueryParams
} from '../types/api';
import type {
  ApiRequestConfig
} from '../types/api-endpoints';
import {
  buildUrl
} from '../types/api-endpoints';
import { apiHealthMonitor } from './api-health-monitor';

// JSON:API helpers — flatten { id, type, attributes } resources into plain objects
export function extractAttributes<T>(
  resource: JsonApiResource<T> | null | undefined
): (T & { id: string }) | null {
  if (!resource) return null;
  return { id: resource.id, ...resource.attributes };
}

export function extractAttributesList<T>(
  response: ApiResponse<JsonApiResource<T>[]> | null | undefined
): (T & { id: string })[] {
  return response?.data?.map((item) => ({ id: item.id, ...item.attributes })) ?? [];
}

// Base configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// CSRF token storage
let csrfToken: string | null = null;

// Function to get CSRF token from API
async function getCsrfToken(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/csrf_token`, {
      credentials: 'include',  // Include cookies
    });

    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token;
      return csrfToken!;
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }

  return '';
}

// Clear CSRF token (call this on logout)
export function clearCsrfToken(): void {
  csrfToken = null;
}

// Base headers (without auth)
const baseHeaders: Record<string, string> = {
  'Content-Type': 'application/vnd.api+json',
  'Accept': 'application/vnd.api+json',
};

// Error class for API errors
export class ApiClientError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// Generic API request function
async function apiRequest<T>(config: ApiRequestConfig, csrfRetried = false): Promise<T> {
  const { method, endpoint, params, data, headers = {}, skip401Handling = false } = config;

  const url = `${API_BASE_URL}${buildUrl(endpoint, params)}`;

  // For the Rails API, we need to include current_user in the data
  let requestData = data;

  // Skip modifying data if X-Skip-Current-User header is present
  if (headers?.['X-Skip-Current-User']) {
    requestData = data;
  } else if (data && typeof data === 'object' && method !== 'GET') {
    // Check if we have a user ID stored (this would come from Auth0 user)
    const userId = localStorage.getItem('current_user_id');

    // If data already has a 'data' property (JSON API format), DON'T modify it
    if ('data' in data && typeof data.data === 'object') {
      requestData = data;
    } else if (userId && !('current_user' in data)) {
      // Otherwise, add current_user for non-JSON:API requests if we have a userId
      requestData = {
        ...data,
        current_user: userId
      };
    } else {
      // No userId or already has current_user, use data as-is
      requestData = data;
    }
  }

  // Clean up custom headers before sending
  const cleanHeaders = { ...headers };
  delete cleanHeaders['X-Skip-Current-User'];

  // Get CSRF token for state-changing requests
  let token = '';
  if (method !== 'GET' && method !== 'HEAD') {
    token = await getCsrfToken();
  }

  // Build final headers with CSRF token
  const finalHeaders = {
    ...baseHeaders,
    ...cleanHeaders,
    ...(token && { 'X-CSRF-Token': token }),
  };

  const requestConfig: RequestInit = {
    method,
    headers: finalHeaders,
    credentials: 'include',  // Important: include cookies in all requests
  };

  // Add body for non-GET requests
  if (requestData && method !== 'GET') {
    requestConfig.body = JSON.stringify(requestData);
  }

  try {
    const response = await fetch(url, requestConfig);

    // Health monitoring: record outcome based on response status.
    // Suppress while a session event is in flight — otherwise Auth0's logout
    // redirect can abort in-flight fetches fast enough to trip the 3-failure
    // threshold before the browser actually navigates.
    if (
      !handlingSessionExpiration &&
      apiHealthMonitor.shouldMonitor() &&
      !apiHealthMonitor.isExcludedEndpoint(url)
    ) {
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        // 2xx and 4xx responses indicate the API is reachable
        apiHealthMonitor.recordSuccess();
      } else if (apiHealthMonitor.isServerError(response.status)) {
        // 5xx errors indicate server issues
        apiHealthMonitor.recordFailure(response.status, url, method);
        if (apiHealthMonitor.shouldRedirect()) {
          apiHealthMonitor.redirectToErrorPage();
          // Return a promise that never resolves to prevent further processing
          return new Promise(() => {});
        }
      }
    }

    if (response.status === 422 && !csrfRetried) {
      const rejected = await response
        .clone()
        .json()
        .then((body) => /authenticity/i.test(body?.error ?? ''))
        .catch(() => false);

      if (rejected) {
        clearCsrfToken();
        return apiRequest<T>(config, true);
      }
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const error = new ApiClientError(
        errorBody?.error || `Request failed with status ${response.status}`,
        response.status,
        errorBody
      );

      // Handle 401 Unauthorized - session has expired or been revoked
      if (response.status === 401) {
        const isRevoked = errorBody?.errors?.some(
          (err: { title?: string }) => err?.title === SESSION_REVOKED_MESSAGE
        );

        if (isRevoked) {
          handleSessionRevoked();
          // Don't surface the rejection — SessionRevokedListener drives the
          // Auth0 logout redirect. Hanging the promise prevents react-query
          // onError handlers from flashing an error toast in the brief window
          // before navigation.
          return new Promise(() => {});
        }

        // Revocation above is always handled; the expired-session recovery is
        // skipped for background/init polls so an expected 401 can't trigger a
        // re-login. The caller still gets the rejection to handle locally.
        if (!skip401Handling) {
          handleSessionExpired();
        }
      }

      throw error;
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    return await response.json();
  } catch (error) {
    // Abort errors are NOT network failures - user navigated away or request was cancelled
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }

    // Network error - API is unreachable.
    // Skip if we're handling a session event (logout redirect aborts fetches
    // in Chrome as TypeError "Failed to fetch", which would falsely trip
    // the health monitor before the browser navigates).
    if (
      !handlingSessionExpiration &&
      !(error instanceof ApiClientError) &&
      apiHealthMonitor.shouldMonitor() &&
      !apiHealthMonitor.isExcludedEndpoint(url)
    ) {
      apiHealthMonitor.recordFailure('network', url, method);
      if (apiHealthMonitor.shouldRedirect()) {
        apiHealthMonitor.redirectToErrorPage();
        // Return a promise that never resolves to prevent further processing
        return new Promise(() => {});
      }
    }

    if (error instanceof ApiClientError) {
      throw error;
    }
    throw new ApiClientError(
      error instanceof Error ? error.message : 'Unknown error occurred'
    );
  }
}

// Track if we're currently handling a session expiration to prevent race conditions
let handlingSessionExpiration = false;

// Helper to check if we're handling session expiration (used by other modules)
export function isHandlingSessionExpiration(): boolean {
  return handlingSessionExpiration;
}

// Cleared by the SessionExpiredListener once it has silently re-minted the
// backend session, so future genuine expirations are handled again.
export function resetSessionExpirationHandling(): void {
  handlingSessionExpiration = false;
}

// Contract with the backend: title of the 401 body when a session has been
// revoked (OIDC backchannel logout, admin-forced logout, password change).
// Backend constant: Api::Authentication::SESSION_REVOKED_MESSAGE
export const SESSION_REVOKED_MESSAGE = 'Session has been revoked';

// Dispatched when the backend reports the current session as revoked. The
// AuthContext listens for this and drives a full Auth0 logout so the browser
// can't silent-reauthenticate and land back in a signed-in state.
export const SESSION_REVOKED_EVENT = 'auth:session-revoked';

// Dispatched when a Prosci admin's backend session cookie has merely expired
// (not revoked). SessionExpiredListener re-mints it from the live Auth0 session
// WITHOUT reloading the page, so in-progress work survives; it falls back to a
// full re-login only if the Auth0 session is gone too.
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

// The session was explicitly revoked (not just expired). Fire an event so
// the app can call Auth0's /v2/logout and terminate the SSO cookie.
function handleSessionRevoked(): void {
  if (handlingSessionExpiration) return;
  handlingSessionExpiration = true;
  window.dispatchEvent(new CustomEvent(SESSION_REVOKED_EVENT));
}

// Handle session expiration
function handleSessionExpired(): void {
  // Prevent multiple simultaneous calls
  if (handlingSessionExpiration) {
    return;
  }
  handlingSessionExpiration = true;

  // Import sessionAuthService dynamically to avoid circular dependencies
  import('../services/session-auth-service').then(({ sessionAuthService }) => {
    // Get admin info BEFORE clearing the session
    const adminInfo = sessionAuthService.getAdminInfo();

    // Check if this is a license admin
    // Primary check: explicit type field
    // Fallback check: if permissions include license_admin permissions
    const isLicenseAdmin =
      adminInfo?.type === 'license_admin' ||
      (adminInfo?.permissions?.some(p => p.startsWith('license_admin.')) &&
       !adminInfo?.permissions?.includes('*'));

    if (isLicenseAdmin) {
      // License admins authenticate via a portal-issued token (not Auth0), so
      // they can't silently recover here — send them back to the portal.
      const portalUrl = import.meta.env.VITE_PORTAL_URL || 'https://portal.prosci.com';

      // Clear the session AFTER determining redirect target but BEFORE redirecting
      sessionAuthService.clearSession();

      // Use location.replace for immediate redirect without history entry
      window.location.replace(portalUrl);
    } else {
      // Prosci admins hold a live Auth0 session. Hand off to SessionExpiredListener,
      // which re-mints the backend cookie WITHOUT a page reload so in-progress work
      // (e.g. a prompt being edited) survives. It falls back to a full re-login only
      // if the Auth0 session is also gone.
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  });
}

// Typed API client methods
export const apiClient = {
  // GET request
  get<T>(endpoint: string, params?: QueryParams, headers?: Record<string, string>, skip401Handling?: boolean): Promise<T> {
    // For GET requests, add current_user to params
    const userId = localStorage.getItem('current_user_id');
    const enhancedParams = userId ? { ...params, current_user: userId } : params;

    return apiRequest<T>({
      method: 'GET',
      endpoint,
      params: enhancedParams,
      headers,
      skip401Handling,
    });
  },

  // POST request
  post<T>(endpoint: string, data?: any, params?: QueryParams, headers?: Record<string, string>, skip401Handling?: boolean): Promise<T> {
    return apiRequest<T>({
      method: 'POST',
      endpoint,
      data,
      params,
      headers,
      skip401Handling,
    });
  },

  // PUT request
  put<T>(endpoint: string, data?: any, params?: QueryParams, headers?: Record<string, string>, skip401Handling?: boolean): Promise<T> {
    return apiRequest<T>({
      method: 'PUT',
      endpoint,
      data,
      params,
      headers,
      skip401Handling,
    });
  },

  // PATCH request
  patch<T>(endpoint: string, data?: any, params?: QueryParams, headers?: Record<string, string>, skip401Handling?: boolean): Promise<T> {
    return apiRequest<T>({
      method: 'PATCH',
      endpoint,
      data,
      params,
      headers,
      skip401Handling,
    });
  },

  // DELETE request
  delete<T>(endpoint: string, params?: QueryParams, headers?: Record<string, string>, skip401Handling?: boolean): Promise<T> {
    return apiRequest<T>({
      method: 'DELETE',
      endpoint,
      params,
      headers,
      skip401Handling,
    });
  },
};

// Helper functions for common patterns

// Extract data from JSON API response
export function extractData<T>(response: ApiResponse<JsonApiResource<T>>): T {
  return response.data.attributes;
}

// Extract array data from JSON API response
export function extractArrayData<T>(response: ApiResponse<JsonApiResource<T>[]>): T[] {
  return response.data.map(item => item.attributes);
}

// Build JSON API request body
export function buildJsonApiBody<T>(attributes: T, type?: string): { data: { attributes: T; type?: string } } {
  return {
    data: {
      attributes,
      ...(type && { type }),
    },
  };
}

// Auth helper functions - now handled by cookies
// Keeping these exports for backward compatibility but they're no-ops
export function setAuthToken(token: string): void {
  // No-op: cookies handle authentication now
}

export function clearAuthToken(): void {
  // Clear CSRF token on logout
  clearCsrfToken();
}

// Request interceptor for adding custom logic
export type RequestInterceptor = (config: ApiRequestConfig) => ApiRequestConfig | Promise<ApiRequestConfig>;
export type ResponseInterceptor = <T>(response: T) => T | Promise<T>;

const requestInterceptors: RequestInterceptor[] = [];
const responseInterceptors: ResponseInterceptor[] = [];

export function addRequestInterceptor(interceptor: RequestInterceptor): void {
  requestInterceptors.push(interceptor);
}

export function addResponseInterceptor(interceptor: ResponseInterceptor): void {
  responseInterceptors.push(interceptor);
}

// Masquerading API helpers
export interface MasqueradingStatus {
  is_masquerading: boolean;
  current_user_id?: string;
  current_user_email?: string;
  original_admin_user_id?: string;
  original_admin_auth0_sub?: string;
}

export interface StopMasqueradingResponse {
  success: boolean;
  message: string;
  admin?: {
    id: string;
    type: string;
    level: string;
    email: string;
    name: string;
    permissions: string[];
  };
  redirect_url?: string;
  original_admin_id?: string;
}

export async function getMasqueradingStatus(): Promise<MasqueradingStatus> {
  try {
    // Background status check — skip the expired-session flow so an expected
    // 401 here can't trigger a re-login.
    return await apiClient.get<MasqueradingStatus>('/api/users/masquerading_status', undefined, undefined, true);
  } catch {
    // If the endpoint fails, assume not masquerading
    return { is_masquerading: false };
  }
}

export async function stopMasquerading(): Promise<StopMasqueradingResponse> {
  return await apiClient.post<StopMasqueradingResponse>('/api/users/stop_masquerading');
}

